/**
 * Agent home statistics. Vendor phone, email, and website taps already live
 * in vendor_clicks. Projects, history reports, shares, and transfers are
 * loaded by the agent-home-activity function with the agent's JWT.
 */

const { trialBannerDue, trialDaysRemaining } = require('./trialStatus');
const { homeownerUsageLevel } = require('./planLimits');

const DAY_MS = 24 * 60 * 60 * 1000;

const PERIODS = [
  { id: 'week', label: 'This week' },
  { id: 'month', label: 'This month' },
  { id: 'all', label: 'All time' },
];

function countWord(count, singular) {
  const amount = Number(count) || 0;
  return amount + ' ' + (amount === 1 ? singular : singular + 's');
}

function stamp(value) {
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
}

function inSpan(value, start, end) {
  const time = stamp(value);
  if (time == null) return false;
  if (start == null) return end == null || time < end;
  return time >= start && (end == null || time < end);
}

function windows(periodId, now) {
  const at = stamp(now) == null ? Date.now() : stamp(now);
  if (periodId === 'all') return { current: { start: null, end: null }, previous: null, noun: '' };
  const days = periodId === 'week' ? 7 : 30;
  const size = days * DAY_MS;
  return {
    current: { start: at - size, end: at + 1 },
    previous: { start: at - size * 2, end: at - size },
    noun: periodId === 'week' ? 'last week' : 'last month',
  };
}

function trend(current, previous, noun) {
  if (!noun) return { text: '', beats: false };
  const delta = current - previous;
  if (delta === 0) return { text: 'Same as ' + noun, beats: false };
  const sign = delta > 0 ? '+' : '−';
  return { text: sign + Math.abs(delta) + ' vs ' + noun, beats: delta > 0 };
}

function countWhere(rows, pick, span) {
  return (rows || []).reduce((sum, row) => (inSpan(pick(row), span.start, span.end) ? sum + 1 : sum), 0);
}

function clickLabel(type) {
  if (type === 'phone') return 'Call';
  if (type === 'email') return 'Email';
  if (type === 'website') return 'Website';
  return '';
}

function personName(row) {
  const name = [row && row.first_name, row && row.last_name].filter(Boolean).join(' ').trim();
  return name || 'Homeowner';
}

function projectDone(project) {
  if (!project) return false;
  if (String(project.status || '').toLowerCase() === 'completed') return true;
  const tasks = Array.isArray(project.tasks) ? project.tasks : [];
  return tasks.length > 0 && tasks.every((task) => task && task.completed === true);
}

function projectWhen(project) {
  if (!project) return '';
  return project.updated_at || '';
}

function spark(rows, pick, span) {
  const start = span.start == null ? (function () {
    const times = (rows || []).map((row) => stamp(pick(row))).filter((time) => time != null);
    return times.length ? Math.min.apply(null, times) : Date.now() - DAY_MS;
  }()) : span.start;
  const end = span.end == null ? Date.now() : span.end;
  const width = Math.max(end - start, 1);
  const buckets = [0, 0, 0, 0, 0];
  (rows || []).forEach((row) => {
    const time = stamp(pick(row));
    if (time == null || time < start || time >= end) return;
    const index = Math.min(4, Math.floor(((time - start) / width) * 5));
    buckets[index] += 1;
  });
  return buckets;
}

function topCounts(pairs) {
  const map = {};
  pairs.forEach((pair) => {
    if (!pair || !pair.name) return;
    const key = pair.name;
    if (!map[key]) map[key] = { name: pair.name, id: pair.id || '', count: 0 };
    map[key].count += 1;
  });
  return Object.keys(map).map((key) => map[key]).sort((a, b) => b.count - a.count).slice(0, 3);
}

function tile(id, label, value, previous, noun, breakdown) {
  const change = trend(value, previous, noun);
  return {
    id,
    label,
    value,
    trend: change.text,
    beats: change.beats,
    breakdown,
  };
}

function summarizeAgentActivity(input, periodId, now) {
  const source = input || {};
  const span = windows(periodId || 'month', now);
  const clicks = source.clicks || [];
  const clients = source.clients || [];
  const tasks = (source.tasks || []).filter((row) => row && row.completed === true);
  const projects = (source.projects || []).filter(projectDone);
  const reports = source.reports || [];
  const shares = source.shares || [];
  const transfers = (source.transfers || []).filter((row) => row && row.claimed_at);
  const current = span.current;
  const previous = span.previous || { start: 0, end: 0 };
  const noun = span.noun;
  const call = clicks.filter((row) => row.click_type === 'phone');
  const email = clicks.filter((row) => row.click_type === 'email');
  const web = clicks.filter((row) => row.click_type === 'website');
  const contacts = countWhere(clicks, (row) => row.clicked_at, current);
  const active = countWhere(clients, (row) => row.last_active, current);
  const doneTasks = countWhere(tasks, (row) => row.completed_at, current);
  const doneProjects = countWhere(projects, projectWhen, current);
  const created = countWhere(reports, (row) => row.created_at, current);
  const shared = countWhere(shares, (row) => row.shared_at || row.created_at, current);
  const fresh = countWhere(clients, (row) => row.created_at || row.date_added, current);
  const handoffs = countWhere(transfers, (row) => row.claimed_at, current);
  const vendors = topCounts(clicks.filter((row) => inSpan(row.clicked_at, current.start, current.end)).map((row) => ({
    name: row.vendor_name,
    id: row.vendor_id,
  })));
  const homeowners = topCounts(tasks.filter((row) => inSpan(row.completed_at, current.start, current.end)).map((row) => ({
    name: row.client_name,
    id: row.client_id,
  })));
  return {
    engagement: [
      tile('contacts', 'Vendor contacts', contacts, countWhere(clicks, (row) => row.clicked_at, previous), noun, {
        kind: 'bars',
        rows: [
          { label: 'Call', value: countWhere(call, (row) => row.clicked_at, current) },
          { label: 'Email', value: countWhere(email, (row) => row.clicked_at, current) },
          { label: 'Website', value: countWhere(web, (row) => row.clicked_at, current) },
        ],
      }),
      tile('active', 'Active homeowners', active, countWhere(clients, (row) => row.last_active, previous), noun, {
        kind: 'note',
        text: 'Homes with a recent visit.',
      }),
    ],
    results: [
      tile('tasks', 'Tasks completed', doneTasks, countWhere(tasks, (row) => row.completed_at, previous), noun, {
        kind: 'spark',
        points: spark(tasks, (row) => row.completed_at, current),
      }),
      tile('projects', 'Projects completed', doneProjects, countWhere(projects, projectWhen, previous), noun, {
        kind: 'note',
        text: 'Checklists where every task is done.',
      }),
      tile('reports', 'History reports created', created, countWhere(reports, (row) => row.created_at, previous), noun, {
        kind: 'bars',
        rows: [
          { label: 'Created', value: created },
          { label: 'Shared', value: shared },
        ],
      }),
    ],
    business: [
      tile('fresh', 'New homeowners', fresh, countWhere(clients, (row) => row.created_at || row.date_added, previous), noun, {
        kind: 'note',
        text: 'Homes added in this range.',
      }),
      tile('handoffs', 'Homes handed off', handoffs, countWhere(transfers, (row) => row.claimed_at, previous), noun, {
        kind: 'note',
        text: 'Homes a buyer claimed.',
      }),
    ],
    vendors,
    homeowners,
  };
}

function trialBannerLine(trialEndsAt, now) {
  if (!trialBannerDue(trialEndsAt, now)) return '';
  const days = trialDaysRemaining(trialEndsAt, now);
  if (days == null) return '';
  if (days <= 0) return 'Your free trial has ended.';
  if (days === 1) return 'Your free trial ends tomorrow.';
  return 'Your free trial ends in ' + days + ' days.';
}

function usageBannerLine(used, limit) {
  const level = homeownerUsageLevel(used, limit);
  if (level === 'warn') return 'Active homeowners are at 80% of the plan limit.';
  if (level === 'full' || level === 'paused') return 'Active homeowners are at the plan limit.';
  return '';
}

function homePlanBanner(profile, usage, now) {
  const lines = [];
  if (profile && profile.subscription_status === 'trialing') {
    const line = trialBannerLine(profile.trial_ends_at, now || new Date());
    if (line) lines.push(line);
  }
  if (usage && usage.used != null && usage.used !== '') {
    const line = usageBannerLine(usage.used, usage.limit);
    if (line) lines.push(line);
  }
  return lines.length ? lines : null;
}

function activeClientCount(clients, agentId) {
  return (clients || []).filter((client) => (
    client
    && client.agent_id === agentId
    && String(client.status || '').toLowerCase() === 'active'
  )).length;
}

function clientNameMap(clients) {
  const map = {};
  (clients || []).forEach((client) => {
    if (client && client.id) map[client.id] = personName(client);
  });
  return map;
}

async function readRows(query) {
  try {
    const result = await query;
    if (!result || result.error) return [];
    return result.data || [];
  } catch (error) {
    return [];
  }
}

async function readProtected(fetchProtected) {
  const empty = { projects: [], reports: [], shares: [], transfers: [] };
  if (typeof fetchProtected !== 'function') return empty;
  try {
    const result = await fetchProtected();
    if (!result || result.ok === false) return empty;
    const data = result.data && typeof result.data === 'object' ? result.data : result;
    return {
      projects: Array.isArray(data.projects) ? data.projects : [],
      reports: Array.isArray(data.reports) ? data.reports : [],
      shares: Array.isArray(data.shares) ? data.shares : [],
      transfers: Array.isArray(data.transfers) ? data.transfers : [],
    };
  } catch (error) {
    return empty;
  }
}

async function loadAgentActivity(supabase, agentId, options) {
  const empty = { clicks: [], clients: [], tasks: [], projects: [], reports: [], shares: [], transfers: [] };
  const protectedRows = await readProtected(options && options.fetchProtected);
  if (!supabase || !agentId || typeof supabase.from !== 'function') {
    return Object.assign({}, empty, protectedRows);
  }
  const clients = await readRows(supabase.from('clients').select('id, first_name, last_name, status, agent_id, created_at, date_added, last_active').eq('agent_id', agentId));
  const ids = clients.map((client) => client.id).filter(Boolean);
  const names = clientNameMap(clients);
  const clicks = await readRows(supabase.from('vendor_clicks').select('click_type, clicked_at, vendor_id, client_id').eq('agent_id', agentId));
  const vendorIds = clicks.map((row) => row.vendor_id).filter(Boolean);
  const vendors = vendorIds.length
    ? await readRows(supabase.from('vendors').select('id, name').in('id', vendorIds))
    : [];
  const vendorNames = {};
  vendors.forEach((vendor) => {
    if (vendor && vendor.id) vendorNames[vendor.id] = vendor.name;
  });
  const tasks = ids.length
    ? await readRows(supabase.from('client_tasks').select('client_id, completed, completed_at').in('client_id', ids))
    : [];
  return {
    clicks: clicks.map((row) => ({
      click_type: row.click_type,
      clicked_at: row.clicked_at,
      vendor_id: row.vendor_id,
      vendor_name: vendorNames[row.vendor_id] || 'Vendor',
      client_id: row.client_id,
    })),
    clients,
    tasks: tasks.map((row) => ({
      completed: row.completed === true,
      completed_at: row.completed_at,
      client_id: row.client_id,
      client_name: names[row.client_id] || 'Homeowner',
    })),
    projects: protectedRows.projects,
    reports: protectedRows.reports,
    shares: protectedRows.shares,
    transfers: protectedRows.transfers,
  };
}

module.exports = {
  DAY_MS,
  PERIODS,
  activeClientCount,
  clickLabel,
  countWord,
  clientNameMap,
  homePlanBanner,
  loadAgentActivity,
  summarizeAgentActivity,
};
